import { Link } from "react-router-dom";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import {
  CommandLineIcon,
  MegaphoneIcon,
  PaintBrushIcon,
  CpuChipIcon,
} from "@heroicons/react/24/outline";

const services = [
  {
    title: "Software Development",
    description:
      "We build enterprise-grade web and mobile applications, SaaS products, and custom software solutions tailored to your business model.",
    icon: CommandLineIcon,
    gradient: "from-blue-500 to-cyan-400",
  },
  {
    title: "Business Consulting",
    description:
      "Strategic guidance and expertise to improve productivity, automate processes, and accelerate your digital transformation journey.",
    icon: CpuChipIcon,
    gradient: "from-violet-500 to-purple-400",
  },
  {
    title: "Digital Marketing",
    description:
      "Data-driven marketing strategies that elevate your brand, reach customers faster, and generate measurable business outcomes.",
    icon: MegaphoneIcon,
    gradient: "from-orange-500 to-amber-400",
  },
  {
    title: "Brand Design",
    description:
      "Comprehensive branding and design services that create unique identification for your products, aligned with your vision.",
    icon: PaintBrushIcon,
    gradient: "from-pink-500 to-rose-400",
  },
];

const portfolioItems = [
  {
    title: "Devdesk Pool",
    description: "A comprehensive pool betting SaaS platform built for POS terminals, Android, iOS, and web.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop",
    tags: ["SaaS", "Betting", "Multi-platform"],
  },
  {
    title: "Auditra",
    description: "AI-powered accounting management solution with a developer-friendly API. Moving businesses from paper to paperless.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2940&auto=format&fit=crop",
    tags: ["AI", "Accounting", "API", "Coming Soon"],
  },
  {
    title: "Corporate Web Platform",
    description: "Enterprise solutions that power digital transformation for global brands.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2815&auto=format&fit=crop",
    tags: ["Web App", "Enterprise"],
  },
];

export default function Feature() {
  return (
    <>
      {/* Services Section */}
      <section className="relative py-24 lg:py-32 bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16">
          <div className="max-w-2xl mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full mb-6 border border-gray-200">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
              <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">What We Do</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-4">
              Services built for <span className="text-[#0186ef]">scale</span>
            </h2>
            <p className="text-lg text-gray-500">
              We're committed to innovation, service excellence, and client success at every stage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 border border-gray-100 overflow-hidden"
              >
                {/* Gradient hover effect */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />
                
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-500 leading-relaxed mb-4">{service.description}</p>
                
                <Link
                  to="/services"
                  className="inline-flex items-center text-sm font-semibold text-[#0186ef] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  Learn more
                  <ArrowLongRightIcon className="ml-1 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-6 border border-blue-100">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
                <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">Our Work</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                Selected projects
              </h2>
            </div>
            <Link
              to="/work"
              className="mt-6 sm:mt-0 inline-flex items-center text-[#0186ef] font-semibold hover:text-[#0070d0] transition-colors group"
            >
              View all work
              <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item, index) => (
              <article
                key={index}
                className="group relative rounded-2xl overflow-hidden bg-gray-900 aspect-[3/4] cursor-pointer"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex gap-2 mb-4">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="inline-flex px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white/80">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-300 leading-relaxed">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Big Quote / CTA */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-16 text-center">
          <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-10">
            DevDesk collaborates with businesses to meet real human needs. Our digital solutions create brands, 
            experiences, and software that <span className="text-[#0186ef]">improve lives</span> every day.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0] transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-0.5 group"
          >
            Let's Collaborate
            <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
}
